import type { WorkExperienceCompany } from '@applicator/shared'
import type { SupabaseClient } from '@supabase/supabase-js'

import { RESUME_SECTIONS } from '../components/ResumeDetail/constants'
import { normalizeWorkExperienceInclusionHierarchy } from '../utils/workExperienceInclusion'
import { cloneWorkExperienceCompanies } from '../utils/workExperienceSort'

const WORK_EXPERIENCE_DISPLAY_ORDER = RESUME_SECTIONS.findIndex(
  (section) => section.type === 'work_experience',
)

export function workExperienceHasContent(companies: WorkExperienceCompany[]): boolean {
  if (!Array.isArray(companies) || companies.length === 0) return false

  return companies.some((company) => {
    const hasName = String(company.name ?? '').trim().length > 0
    const roles = company.roles ?? []
    const hasRoles = roles.length > 0
    return hasName || hasRoles
  })
}

function parseWorkExperienceContent(raw: unknown): WorkExperienceCompany[] | null {
  if (!Array.isArray(raw)) return null
  const cloned = cloneWorkExperienceCompanies(raw as WorkExperienceCompany[])
  const normalized = normalizeWorkExperienceInclusionHierarchy(cloned)
  return workExperienceHasContent(normalized) ? normalized : null
}

/** Most recently updated resume work_experience for the account (optional exclude). */
export async function fetchAccountWorkExperience(
  supabase: SupabaseClient,
  options: { excludeResumeId?: string } = {},
): Promise<WorkExperienceCompany[] | null> {
  const { excludeResumeId } = options

  const {
    data: { user },
    error: authError,
  } = await supabase.auth.getUser()
  if (authError) throw authError
  if (!user) return null

  const { data: resumes, error: resumesError } = await supabase
    .from('resumes')
    .select('id, updated_at')
    .eq('user_id', user.id)
    .order('updated_at', { ascending: false })

  if (resumesError) throw resumesError

  const resumeRows = (resumes ?? []).filter((resume) => resume.id !== excludeResumeId)
  if (resumeRows.length === 0) return null

  const resumeIds = resumeRows.map((resume) => resume.id)
  const { data: contentRows, error: contentError } = await supabase
    .from('resume_content')
    .select('resume_id, content')
    .in('resume_id', resumeIds)
    .eq('section_type', 'work_experience')

  if (contentError) throw contentError

  const contentByResume = new Map<string, unknown>()
  for (const row of contentRows ?? []) {
    contentByResume.set(row.resume_id, row.content)
  }

  for (const resume of resumeRows) {
    const parsed = parseWorkExperienceContent(contentByResume.get(resume.id))
    if (parsed) {
      return parsed
    }
  }

  return null
}

/** Upsert account work experience to every resume owned by the user. */
export async function syncAccountWorkExperienceToAllResumes(
  supabase: SupabaseClient,
  content: WorkExperienceCompany[],
  options: { excludeResumeId?: string } = {},
): Promise<void> {
  const { excludeResumeId } = options

  const {
    data: { user },
    error: authError,
  } = await supabase.auth.getUser()
  if (authError) throw authError
  if (!user) return

  const { data: resumes, error: resumesError } = await supabase
    .from('resumes')
    .select('id')
    .eq('user_id', user.id)

  if (resumesError) throw resumesError

  const resumeIds = (resumes ?? [])
    .map((resume) => resume.id)
    .filter((id) => id !== excludeResumeId)

  if (resumeIds.length === 0) return

  const rows = resumeIds.map((resumeId) => ({
    resume_id: resumeId,
    section_type: 'work_experience' as const,
    content: normalizeWorkExperienceInclusionHierarchy(cloneWorkExperienceCompanies(content)),
    display_order: WORK_EXPERIENCE_DISPLAY_ORDER,
  }))

  const { error: upsertError } = await supabase.from('resume_content').upsert(rows, {
    onConflict: 'resume_id,section_type',
    ignoreDuplicates: false,
  })

  if (upsertError) throw upsertError
}
